import { useEffect, useMemo, useState } from "react";
import { Heart, Trash2 } from "lucide-react";
import { fetchProduct, type ShopifyProduct } from "@/lib/shopify";
import { ProductCard } from "@/components/ProductCard";
import { useWishlist } from "@/hooks/useWishlist";
import { useT } from "@/lib/i18n";
import { LocaleLink } from "@/components/LocaleLink";

export const wishlistHeadDa = () => ({
  meta: [
    { title: "Min ønskeliste — Farveladeland" },
    {
      name: "description",
      content:
        "De stykker du har gemt til senere. Ønskelisten ligger lokalt i din browser — ingen konto nødvendig.",
    },
    { property: "og:title", content: "Min ønskeliste — Farveladeland" },
    { property: "og:description", content: "De stykker du har gemt til senere." },
    { name: "robots", content: "noindex, follow" },
  ],
});

export const wishlistHeadEn = () => ({
  meta: [
    { title: "My wishlist — Farveladeland" },
    {
      name: "description",
      content:
        "The pieces you've saved for later. Your wishlist lives locally in your browser — no account needed.",
    },
    { property: "og:title", content: "My wishlist — Farveladeland" },
    { property: "og:description", content: "Pieces you've saved for later." },
    { name: "robots", content: "noindex, follow" },
  ],
});

export function WishlistPage() {
  const { handles, remove, clear } = useWishlist();
  const { t, tn } = useT();
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [missing, setMissing] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (handles.length === 0) {
        setProducts([]);
        setMissing([]);
        return;
      }
      setLoading(true);
      const results = await Promise.all(
        handles.map(async (h) => {
          try {
            const p = await fetchProduct(h);
            return { handle: h, product: p };
          } catch {
            return { handle: h, product: null };
          }
        })
      );
      if (cancelled) return;
      const ok: ShopifyProduct[] = [];
      const gone: string[] = [];
      for (const r of results) {
        if (r.product) ok.push({ node: r.product });
        else gone.push(r.handle);
      }
      setProducts(ok);
      setMissing(gone);
      setLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [handles]);

  const isEmpty = handles.length === 0;
  const headerCount = useMemo(() => tn("wishlist.count", handles.length), [handles.length, tn]);

  return (
    <>
      <section className="pt-36 pb-10 md:pt-44 md:pb-14">
        <div className="mx-auto max-w-[1500px] px-6 md:px-10">
          <div className="grid gap-10 md:grid-cols-12 md:gap-16">
            <div className="md:col-span-7">
              <p className="eyebrow">{t("wishlist.eyebrow")}</p>
              <h1 className="mt-3 font-display text-[clamp(2.5rem,6.5vw,5.5rem)] leading-[0.98] tracking-tight">
                {t("wishlist.title")}
              </h1>
              {!isEmpty && (
                <p className="mt-4 text-xs uppercase tracking-[0.22em] text-muted-foreground">
                  {headerCount}
                </p>
              )}
            </div>
            <div className="flex md:col-span-5 md:items-end md:justify-end">
              {!isEmpty && (
                <button
                  onClick={clear}
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-muted-foreground hover:text-terracotta"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  {t("wishlist.clear")}
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1500px] px-6 pb-24 md:px-10 md:pb-32">
        {isEmpty ? (
          <div className="flex flex-col items-center gap-6 border-y border-border py-24 text-center">
            <Heart className="h-10 w-10 text-foreground/30" strokeWidth={1.4} />
            <div>
              <p className="font-display text-2xl text-foreground/80">
                {t("wishlist.empty")}
              </p>
              <p className="mt-2 max-w-md text-sm text-muted-foreground">
                {t("wishlist.emptyHelp")}
              </p>
            </div>
            <LocaleLink
              to="/collections"
              className="inline-flex items-center gap-2 rounded-full bg-foreground px-6 py-3 text-xs font-medium uppercase tracking-[0.22em] text-background hover:bg-terracotta"
            >
              {t("wishlist.browse")} →
            </LocaleLink>
          </div>
        ) : loading && products.length === 0 ? (
          <p className="py-12 text-center text-sm text-muted-foreground">…</p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-x-4 gap-y-12 sm:grid-cols-3 lg:grid-cols-4">
              {products.map((p) => (
                <ProductCard key={p.node.id} product={p} />
              ))}
            </div>

            {missing.length > 0 && (
              <div className="mt-14 rounded-sm border border-border bg-secondary/40 px-6 py-5">
                <p className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                  {t("wishlist.unavailable")}
                </p>
                <ul className="mt-3 flex flex-wrap gap-2">
                  {missing.map((h) => (
                    <li key={h}>
                      <button
                        onClick={() => remove(h)}
                        className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5 text-xs text-foreground/70 hover:border-foreground hover:text-foreground"
                      >
                        {h}
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
}
